import React, { useEffect, useState } from "react";
import { Sparkles, X, AlertTriangle, Clock, Gift } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    getLaunchPromoStatus,
    activateLaunchPromo,
} from "@/lib/promo";
import { errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";

export function LaunchPromoBanner({ onChange }) {
    const [status, setStatus] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const s = await getLaunchPromoStatus();
            setStatus(s);
        } catch (_) {
            setStatus({ eligible: false, claimed: false, active: false });
        }
    };

    useEffect(() => {
        load();
    }, []);

    const activate = async () => {
        try {
            setBusy(true);
            const res = await activateLaunchPromo();
            toast.success("🎉 Gold Trial activated for 30 days!");
            if (res.supersede_warning) {
                toast.warning(res.supersede_warning, { duration: 6000 });
            }
            setConfirmOpen(false);
            await load();
            onChange?.();
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    if (!status) return null;

    // 1) Active + expiring soon (0..3 days) — amber alert
    if (status.active && status.days_remaining != null && status.days_remaining <= 3) {
        return (
            <div
                className="mt-3 flex items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3"
                data-testid="launch-promo-expiring-alert"
            >
                <Clock size={18} className="shrink-0 text-amber-700" />
                <div className="flex-1 text-sm">
                    <div className="font-semibold text-amber-900">
                        Your Gold Trial ends in {status.days_remaining} day
                        {status.days_remaining !== 1 ? "s" : ""}
                    </div>
                    <div className="text-xs text-amber-800">
                        Upgrade before {formatDate(status.expires_at)} to keep premium features.
                    </div>
                </div>
                <a
                    href="https://wa.me/919000000000?text=Hi%20OfferNest%2C%20I%27d%20like%20to%20upgrade%20my%20plan"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
                    data-testid="launch-promo-upgrade-link"
                >
                    View plans
                </a>
            </div>
        );
    }

    // 2) Active + still healthy — small pill
    if (status.active) {
        return (
            <div
                className="mt-3 flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-4 py-2 text-xs"
                data-testid="launch-promo-active-pill"
            >
                <Sparkles size={14} className="text-emerald-700" />
                <span className="font-semibold text-emerald-900">
                    Gold Trial Active
                </span>
                <span className="text-emerald-700">
                    · ends {formatDate(status.expires_at)}
                </span>
                <span className="ml-auto rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white" data-testid="launch-promo-days-left">
                    {status.days_remaining ?? "—"}d left
                </span>
            </div>
        );
    }

    // 3) Expired trial — red alert
    if (status.claimed && !status.active) {
        return (
            <div
                className="mt-3 flex items-center gap-3 rounded-2xl border border-rose-300 bg-rose-50 px-4 py-3"
                data-testid="launch-promo-expired-alert"
            >
                <AlertTriangle size={18} className="shrink-0 text-rose-700" />
                <div className="flex-1 text-sm">
                    <div className="font-semibold text-rose-900">
                        Your Gold Trial has ended
                    </div>
                    <div className="text-xs text-rose-800">
                        Upgrade to keep Gold features on your offers.
                    </div>
                </div>
                <a
                    href="https://wa.me/919000000000?text=Hi%20OfferNest%2C%20I%27d%20like%20to%20upgrade%20my%20plan"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                >
                    Upgrade
                </a>
            </div>
        );
    }

    // 4) Eligible — the big colourful banner
    if (status.eligible) {
        return (
            <>
                <div
                    className="mt-3 overflow-hidden rounded-2xl border border-orange-500 bg-gradient-to-br from-orange-500 via-orange-400 to-amber-400 p-4 text-white shadow-md"
                    data-testid="launch-promo-banner"
                >
                    <div className="flex items-start gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/20 backdrop-blur">
                            <Gift size={22} />
                        </div>
                        <div className="flex-1">
                            <div className="text-[10px] font-bold uppercase tracking-wider opacity-90">
                                🎉 Launch Promo
                            </div>
                            <div className="mt-0.5 text-lg font-extrabold leading-tight">
                                Get 1 Month of Premium Banner Ads for FREE
                            </div>
                            <div className="mt-1 text-xs opacity-90">
                                Activate your Gold trial —{" "}
                                <span className="line-through opacity-70">₹3,999 value</span>{" "}
                                <b>free forever, no card needed</b>.
                            </div>
                        </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                            onClick={() => setConfirmOpen(true)}
                            className="bg-white font-bold text-orange-700 hover:bg-orange-50"
                            data-testid="launch-promo-activate-btn"
                        >
                            Activate Now
                        </Button>
                        <AlertDialog>
                            <AlertDialogAction asChild>
                                <button
                                    className="rounded-md bg-white/10 px-3 py-1.5 text-sm font-semibold text-white ring-1 ring-white/40 hover:bg-white/20"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        toast.info(
                                            "Free Gold plan for 30 days — unlimited coupons/mo, premium listing, priority support. One-time offer.",
                                        );
                                    }}
                                    data-testid="launch-promo-learn-more"
                                >
                                    Learn more
                                </button>
                            </AlertDialogAction>
                        </AlertDialog>
                    </div>
                </div>

                <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                    <AlertDialogContent data-testid="launch-promo-confirm-dialog">
                        <AlertDialogHeader>
                            <AlertDialogTitle>Activate your Gold Trial?</AlertDialogTitle>
                            <AlertDialogDescription>
                                You'll get 30 days of Gold plan for free — no
                                card required. This is a one-time offer per
                                business.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="launch-promo-cancel-btn">
                                Not now
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={activate}
                                disabled={busy}
                                className="nest-gradient text-white"
                                data-testid="launch-promo-confirm-btn"
                            >
                                {busy ? "Activating…" : "Yes, activate"}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </>
        );
    }

    return null;
}

export default LaunchPromoBanner;
